import { useState, useEffect, useCallback, useMemo } from 'react';

export interface ContactInfo {
  email: string;
  name?: string;
  company?: string;
  domain?: string;
  linkedinUrl?: string;
  githubUsername?: string;
}

export interface ContactPhotoData {
  photoUrl?: string;
  source: 'clearbit' | 'google' | 'linkedin' | 'github' | 'company_favicon' | 'gravatar' | 'generated';
  fallbackInitials: string;
  fallbackColor: string;
  companyLogoUrl?: string;
  cached: boolean;
}

export interface ContactWithPhoto extends ContactInfo {
  photoData?: ContactPhotoData;
  isLoading?: boolean;
  error?: string;
}

export interface UseContactPhotosOptions {
  enableAutoEnrich?: boolean;
  batchSize?: number;
  retryAttempts?: number;
}

export interface UseContactPhotosReturn {
  contacts: ContactWithPhoto[];
  enrichContact: (contact: ContactInfo) => Promise<void>;
  enrichContacts: (contacts: ContactInfo[]) => Promise<void>;
  getContactPhoto: (email: string) => ContactPhotoData | undefined;
  isEnriching: boolean;
  hasErrors: boolean;
  clearErrors: () => void;
  cacheStats?: { size: number; expiredCount: number };
  clearCache: () => Promise<void>;
}

export function useContactPhotos(
  initialContacts: ContactInfo[] = [],
  options: UseContactPhotosOptions = {}
): UseContactPhotosReturn {
  const {
    enableAutoEnrich = true,
    batchSize = 10,
    retryAttempts = 2
  } = options;

  const [contacts, setContacts] = useState<ContactWithPhoto[]>(
    initialContacts.map(contact => ({ ...contact }))
  );
  const [isEnriching, setIsEnriching] = useState(false);
  const [cacheStats, setCacheStats] = useState<{ size: number; expiredCount: number }>();

  // Update contacts when initialContacts changes
  useEffect(() => {
    setContacts(prev => {
      const existingEmails = new Set(prev.map(c => c.email));
      const newContacts = initialContacts
        .filter(contact => !existingEmails.has(contact.email))
        .map(contact => ({ ...contact }));
      
      return [...prev, ...newContacts];
    });
  }, [initialContacts]);

  // Auto-enrich contacts when they're added
  useEffect(() => {
    if (enableAutoEnrich) {
      const unenrichedContacts = contacts.filter(
        contact => !contact.photoData && !contact.isLoading && !contact.error
      );
      
      if (unenrichedContacts.length > 0) {
        enrichContacts(unenrichedContacts);
      }
    }
  }, [contacts, enableAutoEnrich]);

  /**
   * Enrich a single contact's photo data
   */
  const enrichContact = useCallback(async (contact: ContactInfo): Promise<void> => {
    setContacts(prev => prev.map(c => 
      c.email === contact.email 
        ? { ...c, isLoading: true, error: undefined }
        : c
    ));

    try {
      const response = await fetch('/api/contacts/photos/single', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(contact)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      setContacts(prev => prev.map(c => 
        c.email === contact.email 
          ? { 
              ...c, 
              photoData: result.photoData,
              isLoading: false,
              error: undefined
            }
          : c
      ));

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to enrich photo';
      
      setContacts(prev => prev.map(c => 
        c.email === contact.email 
          ? { ...c, isLoading: false, error: errorMessage }
          : c
      ));

      console.warn('Photo enrichment failed for contact:', contact.email, errorMessage);
    }
  }, []);

  /**
   * Enrich multiple contacts' photo data in batches
   */
  const enrichContacts = useCallback(async (contactsToEnrich: ContactInfo[]): Promise<void> => {
    if (contactsToEnrich.length === 0) return;

    setIsEnriching(true);

    try {
      // Mark all contacts as loading
      setContacts(prev => prev.map(c => {
        const shouldEnrich = contactsToEnrich.some(contact => contact.email === c.email);
        return shouldEnrich 
          ? { ...c, isLoading: true, error: undefined }
          : c;
      }));

      // Process in batches to avoid overwhelming the API
      for (let i = 0; i < contactsToEnrich.length; i += batchSize) {
        const batch = contactsToEnrich.slice(i, i + batchSize);
        
        try {
          const response = await fetch('/api/contacts/photos/enrich', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ contacts: batch })
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const { results } = await response.json();

          // Update contacts with results
          setContacts(prev => prev.map(c => {
            const result = results.find((r: any) => r.email === c.email);
            if (result) {
              return {
                ...c,
                photoData: result.success ? result.photoData : undefined,
                isLoading: false,
                error: result.success ? undefined : (result.error || 'Enrichment failed')
              };
            }
            return c;
          }));

        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Batch enrichment failed';
          
          // Mark batch as failed
          setContacts(prev => prev.map(c => {
            const isInBatch = batch.some(contact => contact.email === c.email);
            return isInBatch 
              ? { ...c, isLoading: false, error: errorMessage }
              : c;
          }));

          console.warn('Batch photo enrichment failed:', errorMessage);
        }

        // Small delay between batches to be respectful to APIs
        if (i + batchSize < contactsToEnrich.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

    } finally {
      setIsEnriching(false);
    }
  }, [batchSize]);

  /**
   * Get photo data for a specific contact by email
   */
  const getContactPhoto = useCallback((email: string): ContactPhotoData | undefined => {
    const contact = contacts.find(c => c.email === email);
    return contact?.photoData;
  }, [contacts]);

  /**
   * Check if there are any errors
   */
  const hasErrors = useMemo(() => {
    return contacts.some(contact => contact.error);
  }, [contacts]);

  /**
   * Clear all errors
   */
  const clearErrors = useCallback(() => {
    setContacts(prev => prev.map(c => ({ ...c, error: undefined })));
  }, []);

  /**
   * Clear photo cache
   */
  const clearCache = useCallback(async (): Promise<void> => {
    try {
      const response = await fetch('/api/contacts/photos/clear-cache', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const { stats } = await response.json();
        setCacheStats(stats);
      }
    } catch (error) {
      console.warn('Failed to clear photo cache:', error);
    }
  }, []);

  /**
   * Load cache stats
   */
  useEffect(() => {
    const loadCacheStats = async () => {
      try {
        const response = await fetch('/api/contacts/photos/cache-stats', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const stats = await response.json();
          setCacheStats(stats);
        }
      } catch (error) {
        console.debug('Failed to load cache stats:', error);
      }
    };

    loadCacheStats();
  }, []);

  return {
    contacts,
    enrichContact,
    enrichContacts,
    getContactPhoto,
    isEnriching,
    hasErrors,
    clearErrors,
    cacheStats,
    clearCache
  };
}

/**
 * Generate fallback photo data for contacts without enriched photos
 */
export function generateFallbackPhoto(contact: ContactInfo): ContactPhotoData {
  const initials = generateInitials(contact.name || contact.email);
  const color = generateColorFromString(contact.email);
  
  return {
    source: 'generated',
    fallbackInitials: initials,
    fallbackColor: color,
    cached: false
  };
}

/**
 * Generate initials from name or email
 */
function generateInitials(nameOrEmail: string): string {
  if (!nameOrEmail) return 'U';

  // If it's an email, use the part before @
  if (nameOrEmail.includes('@')) {
    nameOrEmail = nameOrEmail.split('@')[0];
  }

  // Split by common separators and take first letters
  const parts = nameOrEmail.split(/[\s\-_.]+/).filter(part => part.length > 0);
  
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  } else if (parts.length === 1 && parts[0].length >= 2) {
    return parts[0].substring(0, 2).toUpperCase();
  } else {
    return parts[0] ? parts[0][0].toUpperCase() : 'U';
  }
}

/**
 * Generate a consistent color based on string
 */
function generateColorFromString(str: string): string {
  const colors = [
    '#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444',
    '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6366F1'
  ];
  
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  const index = Math.abs(hash) % colors.length;
  return colors[index];
} 